import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Stack,
  Text,
  Button,
  Box,
  ThemeIcon,
  Loader,
  Center,
  Collapse,
  Group,
} from '@mantine/core';
import { Users, ChevronRight, ChevronLeft, ChevronDown } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import {
  cohortCoachesApi,
  type CohortCoachWithDetails,
  type CohortCoachAvailabilityStatus,
} from '../../../services/api/cohortCoaches';
import type { GroupWithMembers } from '../../../services/api/groups';
import type { SessionWithDetails } from '../../../services/api/sessions';
import { CoachCard, type CoachGroupAvailability } from './CoachCard';
import { getEffectiveConflictsForGroup, type GroupSessionsMap } from './groups/coachConflictUtils';
import classes from './CoachesPanel.module.css';

interface CoachesPanelProps {
  cohortId: string;
  groups: GroupWithMembers[];
  onSendReminder: (coach: CohortCoachWithDetails) => void;
  onCoachSelect?: (coach: CohortCoachWithDetails) => void;
  selectedCoachId?: string;
  refreshTrigger?: number;
  groupSessionsMap?: Record<string, SessionWithDetails[]>;
  // MED-977: configured group-coaching session count, used by the per-group
  // availability widget (e.g. "4/4 sessions") instead of a hardcoded 6.
  configuredGroupCoachingSessionCount?: number | null;
}

// Filter coaches who have responded with at least some availability
const filterEligibleCoaches = (coaches: CohortCoachWithDetails[]): CohortCoachWithDetails[] => {
  return coaches.filter((coach) => {
    // Must have availability requested
    if (coach.availability_status === 'not_requested') {
      return false;
    }
    // Show pending coaches (so we can send reminders)
    if (coach.availability_status === 'pending_response') {
      return true;
    }
    // Don't show coaches who responded as completely unavailable
    if (coach.availability_status === 'unavailable') {
      return false;
    }
    // Show available and partially available coaches
    return true;
  });
};

// Sort coaches: most availability first, then by satisfaction, then by cohort count
const sortCoaches = (coaches: CohortCoachWithDetails[]): CohortCoachWithDetails[] => {
  return [...coaches].sort((a, b) => {
    // 1. Available/Partial first, Pending last
    const statusOrder: Record<CohortCoachAvailabilityStatus, number> = {
      available: 0,
      partially_available: 1,
      pending_response: 2,
      not_requested: 3,
      unavailable: 4,
    };
    const statusDiff = statusOrder[a.availability_status] - statusOrder[b.availability_status];
    if (statusDiff !== 0) return statusDiff;

    // 2. Within same status, sort by availability percent (higher first)
    const availDiff = (b.availability_percent || 0) - (a.availability_percent || 0);
    if (availDiff !== 0) return availDiff;

    // 3. Then by satisfaction score (higher first)
    const satA = a.satisfaction_score ?? 0;
    const satB = b.satisfaction_score ?? 0;
    const satDiff = satB - satA;
    if (satDiff !== 0) return satDiff;

    // 4. Then by number of groups assigned (more experienced coaches first)
    return (b.group_count || 0) - (a.group_count || 0);
  });
};

// Calculate coach-group availability matching based on time slot matching.
// MED-977: totalSessions comes from the cohort's configured group-coaching
// session count (not a hardcoded fallback). Caller passes 0 when no count is
// configured — yields "0/0" which reads as "not yet configured".
// MED-978: per-group conflicts come from the shared
// `getEffectiveConflictsForGroup` helper so this surface and the Groups
// assignment screen agree on which dates count against which group.
const calculateGroupAvailability = (
  coach: CohortCoachWithDetails,
  groups: GroupWithMembers[],
  totalSessions: number,
  groupSessionsMap: GroupSessionsMap,
): CoachGroupAvailability[] => {

  // If coach hasn't responded yet, show all groups with unknown availability
  if (coach.availability_status === 'pending_response') {
    return groups.slice(0, 3).map((group) => ({
      groupId: group.id,
      groupName: group.name,
      availableSessions: null,
      totalSessions,
      status: 'unknown' as const,
    }));
  }

  // If coach is unavailable, show no availability for any group
  if (coach.availability_status === 'unavailable') {
    return groups.slice(0, 3).map((group) => ({
      groupId: group.id,
      groupName: group.name,
      availableSessions: 0,
      totalSessions,
      status: 'unknown' as const,
    }));
  }

  // Match coach's confirmed time slots with group schedules
  return groups.slice(0, 3).map((group) => {
    // Groups without time slots can't be matched to any coach availability
    if (!group.scheduled_time_slot_id) {
      return {
        groupId: group.id,
        groupName: group.name,
        availableSessions: null,
        totalSessions,
        status: 'unknown' as const,
      };
    }

    // Coach has no confirmed time slots
    if (!coach.confirmed_time_slots || coach.confirmed_time_slots.length === 0) {
      return {
        groupId: group.id,
        groupName: group.name,
        availableSessions: 0,
        totalSessions,
        status: 'unknown' as const,
      };
    }

    // Check if coach has confirmed this group's time slot (compare UUIDs)
    const hasMatchingSlot = coach.confirmed_time_slots.includes(group.scheduled_time_slot_id);

    if (!hasMatchingSlot) {
      return {
        groupId: group.id,
        groupName: group.name,
        availableSessions: 0,
        totalSessions,
        status: 'unknown' as const,
      };
    }

    // Shared per-group conflict rule: only dates the coach flagged AND that
    // match one of THIS group's session slot-dates count. Matches the rule
    // applied by the Groups assignment screen via getSchedulingConflicts.
    const effectiveConflicts = getEffectiveConflictsForGroup(
      coach,
      group,
      groupSessionsMap[group.id],
    );
    const availableSessions = Math.max(0, totalSessions - effectiveConflicts.length);
    const isPartial = effectiveConflicts.length > 0;

    return {
      groupId: group.id,
      groupName: group.name,
      availableSessions,
      totalSessions,
      status: isPartial ? 'partial' as const : 'full' as const,
      conflicts: isPartial ? effectiveConflicts : undefined,
    };
  });
};

export function CoachesPanel({
  cohortId,
  groups,
  onSendReminder,
  onCoachSelect,
  selectedCoachId,
  refreshTrigger,
  groupSessionsMap,
  configuredGroupCoachingSessionCount,
}: CoachesPanelProps) {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [coaches, setCoaches] = useState<CohortCoachWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // Fetch coaches
  const fetchCoaches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await cohortCoachesApi.list(cohortId, {
        limit: 50, // Get all coaches for the panel
      });
      setCoaches(response.data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  }, [cohortId]);

  useEffect(() => {
    fetchCoaches();
  }, [fetchCoaches, refreshTrigger]);

  // Filter and sort coaches
  const displayCoaches = useMemo(() => {
    const eligible = filterEligibleCoaches(coaches);
    return sortCoaches(eligible);
  }, [coaches]);

  // Check if we can scroll left/right
  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 10);
      setCanScrollRight(container.scrollWidth > container.clientWidth + container.scrollLeft + 10);
    }
  }, []);

  useEffect(() => {
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, [checkScrollability, displayCoaches]);

  // Handle scroll left
  const handleScrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -300, behavior: 'smooth' });
      setTimeout(checkScrollability, 300);
    }
  };

  // Handle scroll right
  const handleScrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 300, behavior: 'smooth' });
      setTimeout(checkScrollability, 300);
    }
  };

  // Navigate to coaches tab
  const handleManageCoaches = () => {
    navigate(`/cohorts/${cohortId}?tab=coaches`);
  };

  // Get max groups from the coach's availability response
  const getMaxGroups = (coach: CohortCoachWithDetails): number | null => {
    return coach.max_groups ?? null;
  };

  const getIsFlexibleGroups = (coach: CohortCoachWithDetails): boolean => {
    return coach.is_flexible_groups ?? false;
  };

  if (loading) {
    return (
      <Box className={classes.panel}>
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Box>
    );
  }

  // Zero state - no eligible coaches
  if (displayCoaches.length === 0) {
    return (
      <Box className={classes.panel}>
        <Stack align="center" gap="md" py="md">
          <ThemeIcon size={60} radius={60} color="gray" variant="light">
            <Users size={30} />
          </ThemeIcon>
          <Box ta="center">
            <Text fw={600} size="lg" mb={4}>No coaches with availability</Text>
            <Text c="dimmed" size="sm" maw={400}>
              Add coaches to this cohort and request their availability to start assigning them to groups.
            </Text>
          </Box>
          <Button
            variant="outline"
            color="gray"
            bg="white"
            style={{ borderColor: '#E8E6E3' }}
            onClick={handleManageCoaches}
          >
            Manage Coaches for Cohort
          </Button>
        </Stack>
      </Box>
    );
  }

  // Populated state
  return (
    <Box className={classes.panel}>
      {/* Header — clickable to toggle collapse */}
      <div className={classes.panelHeader} style={isExpanded ? undefined : { marginBottom: 0 }}>
        <Group
          gap="xs"
          style={{ cursor: 'pointer' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <ChevronDown
            size={16}
            style={{
              transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 200ms',
              color: '#888888',
            }}
          />
          <div>
            <div className={classes.panelTitle}>
              {isExpanded ? 'Coaches' : `Coaches (${displayCoaches.length})`}
            </div>
            {isExpanded && (
              <div className={classes.panelSubtitle}>Click a coach to assign to groups</div>
            )}
          </div>
        </Group>
        <Button
          className={classes.panelHeaderAction}
          variant="outline"
          color="gray"
          bg="white"
          size="sm"
          style={{ borderColor: '#E8E6E3' }}
          onClick={handleManageCoaches}
        >
          Manage Coaches for Cohort
        </Button>
      </div>

      <Collapse in={isExpanded}>
        {/* Coach Cards - Horizontal Scroll */}
        <div className={classes.scrollContainer}>
          {/* Scroll Left Arrow */}
          {canScrollLeft && (
            <button
              className={classes.scrollButtonLeft}
              onClick={handleScrollLeft}
              type="button"
              aria-label="Scroll left"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className={classes.scrollWrapper}
            onScroll={checkScrollability}
          >
            <div className={classes.cardsRow}>
              {displayCoaches.map((coach) => (
                <CoachCard
                  key={coach.id}
                  coach={coach}
                  maxGroups={getMaxGroups(coach)}
                  isFlexibleGroups={getIsFlexibleGroups(coach)}
                  groupAvailability={calculateGroupAvailability(coach, groups, configuredGroupCoachingSessionCount ?? 0, groupSessionsMap ?? {})}
                  extraGroupsCount={Math.max(0, groups.length - 3)}
                  onSendReminder={() => onSendReminder(coach)}
                  onSelect={() => onCoachSelect?.(coach)}
                  isSelected={selectedCoachId === coach.id}
                  groupSessionsMap={groupSessionsMap}
                />
              ))}
            </div>
          </div>

          {/* Scroll Right Arrow */}
          {canScrollRight && (
            <button
              className={classes.scrollButtonRight}
              onClick={handleScrollRight}
              type="button"
              aria-label="Scroll right"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>
      </Collapse>
    </Box>
  );
}

export default CoachesPanel;
